import { authFetch, getBackendUser, onAuthChange } from './auth.js';
import { modalOpen, modalClose } from './modal-state.js';

const apiUrl = import.meta.env.VITE_API_BASE_URL;

let activeStream = null;
let initialized = false;

let modalEl = null;
let previewEl = null;
let feedbackEl = null;
let saveBtn = null;
let cancelBtn = null;
let closeBtn = null;

let optionsEl = null;
let deleteOption = null;
let muteOption = null;
let banOption = null;
let reasonSection = null;
let reasonInput = null;
let charCount = null;

let selectedAction = 'delete';

let pendingMessageId = null;
let pendingMessageEl = null;
let pendingUserId = null;

let delegationContainer = null;
let delegationClickHandler = null;
let delegationKeyHandler = null;

function getModalEls() {
  if (modalEl) return;
  modalEl = document.getElementById('messageDeleteModal');
  previewEl = document.getElementById('messageDeletePreview');
  feedbackEl = document.getElementById('messageDeleteFeedback');
  saveBtn = document.getElementById('messageDeleteConfirm');
  cancelBtn = document.getElementById('messageDeleteCancel');
  closeBtn = document.getElementById('messageDeleteClose');
  optionsEl = document.getElementById('messageAdminOptions');
  deleteOption = document.getElementById('messageOptionDelete');
  muteOption = document.getElementById('messageOptionMute');
  banOption = document.getElementById('messageOptionBan');
  reasonSection = document.getElementById('messageAdminReasonSection');
  reasonInput = document.getElementById('messageActionReason');
  charCount = document.getElementById('messageActionCharCount');
}

function init() {
  if (initialized) return;
  getModalEls();
  if (!modalEl) return;
  initialized = true;

  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);
  saveBtn?.addEventListener('click', handleSubmit);

  deleteOption?.addEventListener('click', () => selectAction('delete'));
  muteOption?.addEventListener('click', () => selectAction('mute'));
  banOption?.addEventListener('click', () => selectAction('ban'));

  reasonInput?.addEventListener('input', () => {
    if (charCount) charCount.textContent = `${reasonInput.value.length} / 300`;
  });

  modalEl.addEventListener('click', (event) => {
    if (event.target === modalEl) closeModal();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modalEl.classList.contains('active')) {
      closeModal();
    }
  });

  onAuthChange(refreshAdminAffordance);
}

function selectAction(action) {
  selectedAction = action;
  syncSelection();
}

function syncSelection() {
  deleteOption?.classList.toggle('is-selected', selectedAction === 'delete');
  muteOption?.classList.toggle('is-selected', selectedAction === 'mute');
  banOption?.classList.toggle('is-selected', selectedAction === 'ban');

  const needsReason = selectedAction === 'mute' || selectedAction === 'ban';
  if (reasonSection) reasonSection.style.display = needsReason ? '' : 'none';
  if (reasonInput && needsReason) {
    reasonInput.placeholder = `${selectedAction} reason (required)`;
  }
}

function resetForm() {
  selectedAction = 'delete';
  if (reasonInput) reasonInput.value = '';
  if (charCount) charCount.textContent = '0 / 300';
  syncSelection();
  if (saveBtn) saveBtn.disabled = false;
  if (cancelBtn) cancelBtn.disabled = false;
  setFeedback('', null);
}

function isAdminForActiveRoom() {
  if (!activeStream) return false;
  const user = getBackendUser();
  return Array.isArray(user?.rooms_admin) && user.rooms_admin.includes(activeStream.name);
}

function setupChatDelegation() {
  const container = document.querySelector('.chat-messages');
  if (!container) return;
  if (delegationContainer === container) return;

  if (delegationContainer && delegationClickHandler) {
    delegationContainer.removeEventListener('click', delegationClickHandler);
  }
  if (delegationContainer && delegationKeyHandler) {
    delegationContainer.removeEventListener('keydown', delegationKeyHandler);
  }

  delegationContainer = container;

  delegationClickHandler = (event) => {
    const contentEl = event.target.closest?.('.chat-content--admin-deletable');
    if (!contentEl || !delegationContainer.contains(contentEl)) return;
    const messageEl = contentEl.closest('.chat-message');
    const messageId = messageEl?.dataset.messageId;
    if (!messageId) return;
    if (!isAdminForActiveRoom()) return;
    event.stopPropagation();
    openModal(messageId, contentEl.textContent || '', messageEl);
  };

  delegationKeyHandler = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const contentEl = event.target.closest?.('.chat-content--admin-deletable');
    if (!contentEl || !delegationContainer.contains(contentEl)) return;
    const messageEl = contentEl.closest('.chat-message');
    const messageId = messageEl?.dataset.messageId;
    if (!messageId) return;
    if (!isAdminForActiveRoom()) return;
    event.preventDefault();
    openModal(messageId, contentEl.textContent || '', messageEl);
  };

  delegationContainer.addEventListener('click', delegationClickHandler);
  delegationContainer.addEventListener('keydown', delegationKeyHandler);
}

function applyAffordance(contentEl, enable) {
  if (!contentEl) return;
  if (enable) {
    contentEl.classList.add('chat-content--admin-deletable');
    contentEl.setAttribute('role', 'button');
    contentEl.setAttribute('tabindex', '0');
    contentEl.title = 'click to moderate this message';
  } else {
    contentEl.classList.remove('chat-content--admin-deletable');
    contentEl.removeAttribute('role');
    contentEl.removeAttribute('tabindex');
    contentEl.removeAttribute('title');
  }
}

function refreshAdminAffordance() {
  const container = document.querySelector('.chat-messages');
  if (!container) return;
  const enable = isAdminForActiveRoom();
  container.querySelectorAll('.chat-message').forEach((msgEl) => {
    if (!msgEl.dataset.messageId) return;
    const contentEl = msgEl.querySelector('.chat-content');
    if (!contentEl) return;
    applyAffordance(contentEl, enable);
  });
}

export function setActiveRoomForMessageAdmin(stream) {
  init();
  activeStream = stream || null;
  setupChatDelegation();
  refreshAdminAffordance();
}

export function clearActiveRoomForMessageAdmin() {
  activeStream = null;
  refreshAdminAffordance();
  if (modalEl?.classList.contains('active')) {
    closeModal();
  }
}

export function markMessageElementForAdmin(messageEl, message) {
  if (!messageEl || !message?.id) return;
  messageEl.dataset.messageId = String(message.id);
  const userObj = message.user;
  const hasUserObj = !!(userObj && typeof userObj === 'object' && Object.keys(userObj).length > 0);
  const userId = hasUserObj ? userObj.id : message.user_id;
  const displayName = hasUserObj ? (userObj.display_name || message.display_name) : message.display_name;
  if (userId) messageEl.dataset.userId = String(userId);
  if (displayName) messageEl.dataset.userName = displayName;
  if (!isAdminForActiveRoom()) return;
  const contentEl = messageEl.querySelector('.chat-content');
  applyAffordance(contentEl, true);
}

function setFeedback(message, kind) {
  if (!feedbackEl) return;
  if (!message) {
    feedbackEl.style.display = 'none';
    feedbackEl.textContent = '';
    feedbackEl.classList.remove('room-admin-feedback--error', 'room-admin-feedback--success');
    return;
  }
  feedbackEl.textContent = message;
  feedbackEl.style.display = 'block';
  feedbackEl.classList.toggle('room-admin-feedback--error', kind === 'error');
  feedbackEl.classList.toggle('room-admin-feedback--success', kind === 'success');
}

function setSubmitting(submitting) {
  if (saveBtn) saveBtn.disabled = submitting;
  if (cancelBtn) cancelBtn.disabled = submitting;
}

function openModal(messageId, previewText, messageEl) {
  init();
  if (!modalEl) return;
  if (!isAdminForActiveRoom()) return;
  pendingMessageId = messageId;
  pendingMessageEl = messageEl || null;
  pendingUserId = messageEl?.dataset.userId || null;

  if (previewEl) previewEl.textContent = previewText || '';
  resetForm();

  if (muteOption) muteOption.style.display = pendingUserId ? '' : 'none';
  if (banOption) banOption.style.display = pendingUserId ? '' : 'none';

  modalEl.classList.add('active');
  modalOpen();
}

function closeModal() {
  if (!modalEl) return;
  if (!modalEl.classList.contains('active')) return;
  modalEl.classList.remove('active');
  modalClose();
  pendingMessageId = null;
  pendingMessageEl = null;
  pendingUserId = null;
}

async function handleSubmit() {
  if (!activeStream || !pendingMessageId) return;

  const action = selectedAction === 'delete' ? null : selectedAction;

  if (action) {
    if (!pendingUserId) {
      setFeedback('cannot mute or ban this user', 'error');
      return;
    }
    const reason = reasonInput?.value.trim() || '';
    if (!reason) {
      setFeedback('reason is required', 'error');
      reasonInput?.focus();
      return;
    }
  }

  setFeedback('', null);
  setSubmitting(true);

  try {
    const hideRes = await authFetch(
      `${apiUrl}/rooms/${encodeURIComponent(activeStream.name)}/messages/${encodeURIComponent(pendingMessageId)}/hide`,
      { method: 'PATCH' }
    );
    let hideData = null;
    try { hideData = await hideRes.json(); } catch (_) { hideData = null; }
    if (!hideRes.ok) {
      const msg = hideData?.error || hideData?.detail || `delete failed (${hideRes.status})`;
      setFeedback(msg, 'error');
      return;
    }

    if (pendingMessageEl?.parentElement) {
      pendingMessageEl.parentElement.removeChild(pendingMessageEl);
    }

    if (action && pendingUserId) {
      const endpoint = action === 'ban' ? 'ban-user' : 'mute-user';
      const reason = reasonInput?.value.trim() || '';
      const actionRes = await authFetch(
        `${apiUrl}/rooms/${encodeURIComponent(activeStream.name)}/${endpoint}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: pendingUserId, reason }),
        }
      );
      let actionData = null;
      try { actionData = await actionRes.json(); } catch (_) { actionData = null; }
      if (!actionRes.ok) {
        const msg = actionData?.error || actionData?.detail || `${action} failed (${actionRes.status})`;
        setFeedback(`message deleted, but ${action} failed: ${msg}`, 'error');
        return;
      }
    }

    closeModal();
  } catch (err) {
    console.warn('Moderate message failed:', err);
    setFeedback('network error, please try again', 'error');
  } finally {
    setSubmitting(false);
  }
}
